import React from "react";
import { motion } from "framer-motion";
import { 
  FlaskRound, 
  Microscope, 
  TestTube, 
  Dna, 
  Beaker, 
  CheckCircle2, 
  Scale, 
  Workflow,
  Package
} from 'lucide-react';
import SEO from "@/components/SEO";
import useTranslation from "@/hooks/useTranslation";

const Services = () => {
  const { t, language } = useTranslation();

  // Use the same clean approach for getting base path as in App.jsx and Navbar.jsx
  const getBasePath = () => {
    const baseUrl = document.baseURI;
    const urlObj = new URL(baseUrl);
    return urlObj.pathname;
  };

  // Get the relative image path based on the current language
  const getRelativeImagePath = (imageName) => {
    // For Spanish paths, we need to go up one directory level
    if (language === 'es') {
      return `../images/${imageName}`;
    }
    // For English paths, use a relative path
    return `images/${imageName}`;
  };

  return (
    <div className="min-h-screen py-16">
      <SEO 
        title={`${t('ourServices')} - NuWav Laboratories`}
        description={t('servicesPageDesc')}
        keywords="clinical diagnostics, molecular testing, COVID-19 testing, influenza testing, RSV testing, laboratory services, NuWav Laboratories, medical testing"
        canonicalUrl="https://nuwavlabs.com/services"
      />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h1 className="text-4xl font-bold mb-6">{t('servicesPageTitle')}</h1>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            {t('servicesPageDesc')}
          </p>
        </motion.div>

        {/* Hero Image */}
        <div className="mb-16 rounded-2xl overflow-hidden shadow-2xl">
          <img 
            className="w-full h-[400px] object-cover"
            alt="Advanced laboratory equipment"
            src={getRelativeImagePath('services-image.jpg')}
          />
        </div>

        {/* Services Grid - Three columns for desktop, one column for mobile */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Core Testing Services */}
          <ServiceSection
            icon={<FlaskRound className="w-12 h-12 text-[#40E0D0]" />}
            title={t('coreTestingServices')}
            items={[
              {
                title: t('generalImmunology'),
                description: t('generalImmunologyDesc')
              },
              {
                title: t('routineChemistry'),
                description: t('routineChemistryDesc')
              },
              {
                title: t('routineHematology'),
                description: t('routineHematologyDesc')
              },
              {
                title: t('urinalysis'),
                description: t('urinalysisDesc')
              },
              {
                title: t('virology'),
                description: t('virologyDesc')
              }
            ]}
          />

          {/* Molecular Testing Services */}
          <ServiceSection
            icon={<Dna className="w-12 h-12 text-[#40E0D0]" />}
            title={t('molecularTestingServices')}
            description={t('molecularTestingDesc')}
            items={[
              {
                title: t('covid19'),
                description: t('covid19Desc')
              },
              {
                title: t('influenza'),
                description: t('influenzaDesc')
              },
              {
                title: t('rsv'),
                description: t('rsvDesc')
              }
            ]}
          />
          
          {/* Reagents Section */}
          <ServiceSection
            icon={<Package className="w-12 h-12 text-[#40E0D0]" />}
            title={t('expandedReagentsTitle')}
            description={t('expandedReagentsDesc')}
            items={[
              {
                title: t('reagentsOffer1'),
                description: ""
              },
              {
                title: t('reagentsOffer2'),
                description: ""
              },
              {
                title: t('reagentsOffer3'),
                description: ""
              },
              {
                title: t('reagentsOffer4'),
                description: ""
              }
            ]}
            //footer={t('reagentsFooter')}
          />
        </div>

        {/* Expandable Capabilities */}
        <div className="mt-16">
          <motion.h2 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-3xl font-bold mb-8 text-center"
          >
            {t('expandableCapabilities')}
          </motion.h2>
          <p className="text-lg text-gray-600 mb-12 text-center max-w-4xl mx-auto">
            {t('expandableCapabilitiesDesc')}
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <CapabilityCard
              icon={<Microscope />}
              title={t('molecularPCR')}
              items={[
                t('multiplexRespiratory'),
                t('gastrointestinalPathogen'),
                t('stiUtiTesting')
              ]}
            />

            <CapabilityCard
              icon={<TestTube />}
              title={t('clinicalChemistry')}
              items={[
                t('cardiacBiomarkers'),
                t('fertilityHormone'),
                t('vitaminPanels'),
                t('infectiousSerology'),
                t('tumorMarkers')
              ]}
            />

            <CapabilityCard
              icon={<Beaker />}
              title={t('hematology')}
              items={[
                t('cellMorphology'),
                t('differentialWBC'),
                t('reticulocyte'),
                t('hematologicDisorders')
              ]}
            />
          </div>
        </div>

        {/* Why Choose Us */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="mt-16 bg-gradient-to-r from-[#40E0D0] to-[#000080] rounded-lg p-8 text-white"
        >
          <h2 className="text-2xl font-bold mb-6">{t('whyChooseUs')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <FeatureCard
              icon={<CheckCircle2 />}
              title={t('fullyCompliant')}
              description={t('fullyCompliantDesc')}
            />
            <FeatureCard
              icon={<Scale />}
              title={t('scalableThroughput')}
              description={t('scalableThroughputDesc')}
            />
            <FeatureCard
              icon={<Workflow />}
              title={t('flexibleProtocols')}
              description={t('flexibleProtocolsDesc')}
            />
            <FeatureCard
              icon={<TestTube />}
              title={t('partnerDriven')}
              description={t('partnerDrivenDesc')}
            />
          </div>
        </motion.div>

        {/* Call to Action */}
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="mt-16 text-center"
        >
          <h2 className="text-3xl font-bold mb-6">{t('readyToExpand')}</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t('readyToExpandDesc')}
          </p>
        </motion.div>
      </div>
    </div>
  );
};

const ServiceSection = ({ icon, title, description, items, footer }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.8 }}
    className="mb-12 bg-white rounded-lg shadow-lg overflow-hidden h-full"
  >
    <div className="p-6 border-b border-gray-100">
      <div className="flex items-center mb-4">
        {icon}
        <h2 
          className="text-2xl font-bold ml-4"
          dangerouslySetInnerHTML={{ __html: title }}
        />
      </div>
      {description && <p className="text-gray-600">{description}</p>}
    </div>
    
    <div className="p-6">
      <ul className="space-y-4">
        {items.map((item, index) => (
          <li key={index} className="flex items-start">
            <span className="w-2 h-2 bg-[#40E0D0] rounded-full mr-3 mt-2"></span>
            <div>
              <h3 className="font-semibold text-[#000080]">{item.title}</h3>
              {item.description && <p className="text-gray-600">{item.description}</p>}
            </div>
          </li>
        ))}
      </ul>
      {footer && <p className="text-gray-600 italic mt-6">{footer}</p>}
    </div>
  </motion.div>
);

const CapabilityCard = ({ icon, title, items }) => (
  <motion.div
    whileHover={{ scale: 1.02 }}
    className="bg-white rounded-lg shadow-lg p-6"
  >
    <div className="text-[#40E0D0] mb-4 flex items-start">
      {React.cloneElement(icon, { className: "w-12 h-12" })}
    </div>
    <h3 className="text-xl font-bold mb-4 text-[#000080]">{title}</h3>
    <ul className="space-y-2">
      {items.map((item, index) => (
        <li key={index} className="flex items-start text-gray-600">
          <span className="w-2 h-2 bg-[#40E0D0] rounded-full mr-3 mt-2"></span>
          <span>{item}</span>
        </li>
      ))}
    </ul>
  </motion.div>
);

const FeatureCard = ({ icon, title, description }) => (
  <div className="flex flex-col items-start">
    <div className="text-white mb-4">
      {React.cloneElement(icon, { className: "w-8 h-8" })}
    </div>
    <h3 className="font-semibold mb-2">{title}</h3>
    <p className="text-sm opacity-90">{description}</p>
  </div>
);

export default Services;
